import { Navigate, RouteObject } from "react-router-dom";
import ProtectedRoute from "@components/ProtectedRoute";
import { PERMISSIONS, canSeeMySpace, usePermissions } from "@utils/permissions";
import { useIsInclude } from "../../RoutersCst";
import CorporateDetailV2Layout from "@pages/Shared/CorporateDetailV2Layout";
import CatalogV2 from "@pages/Contents/V2/Corporate/Catalog";
import AddActivityV2 from "@pages/Contents/V2/Corporate/Catalog/AddActivity";
import EditActivityV2 from "@pages/Contents/V2/Corporate/Catalog/EditActivity";
import EditionsListPage from "@pages/Contents/V2/Corporate/Catalog/Editions/EditionsListPage";
import AddEditionPage from "@pages/Contents/V2/Corporate/Catalog/Editions/AddEditionPage";
import EditionDetailPage from "@pages/Contents/V2/Corporate/Catalog/Editions/EditionDetailPage";
import AddCourseV2 from "@pages/Contents/V2/Corporate/Catalog/AddCourse";
import EditCourseV2 from "@pages/Contents/V2/Corporate/Catalog/EditCourse";
import AddPathV2 from "@pages/Contents/V2/Corporate/Catalog/AddPath";
import EditPathV2 from "@pages/Contents/V2/Corporate/Catalog/EditPath";
import EditDraftV2 from "@pages/Contents/V2/Corporate/Catalog/EditDraft";
import CatalogLODetail from "@pages/Contents/V2/Corporate/Catalog/LODetail";
import UsersGroupsV2 from "@pages/Contents/V2/Corporate/UsersGroups";
import AddUserV2 from "@pages/Contents/V2/Corporate/UsersGroups/AddUser";
import EditUserV2 from "@pages/Contents/V2/Corporate/UsersGroups/EditUser";
import AddStaticGroupV2 from "@pages/Contents/V2/Corporate/UsersGroups/AddStaticGroup";
import AddDynamicGroupV2 from "@pages/Contents/V2/Corporate/UsersGroups/AddDynamicGroup";
import EditStaticGroupV2 from "@pages/Contents/V2/Corporate/UsersGroups/EditStaticGroup";
import EditDynamicGroupV2 from "@pages/Contents/V2/Corporate/UsersGroups/EditDynamicGroup";
import StaticGroupDetailV2 from "@pages/Contents/V2/Corporate/UsersGroups/GroupDetail";
import DynamicGroupDetailV2 from "@pages/Contents/V2/Corporate/UsersGroups/DynamicGroupDetail";
import UserDetailV2 from "@pages/Contents/V2/Corporate/UsersGroups/UserDetail";
import MySpace from "@pages/Contents/V2/Corporate/MySpace";

const useV2Routes = (): RouteObject => {
  const { user, isInclude } = useIsInclude();
  const { can, canAny } = usePermissions();

  return {
    path: "v2",
    children: [
      {
        path: "corporate/:id/detail",
        element: (
          <ProtectedRoute isAllowed={!!user}>
            <CorporateDetailV2Layout />
          </ProtectedRoute>
        ),
        children: [
          {
            path: "catalog",
            element: (
              <ProtectedRoute
                isAllowed={can([
                  PERMISSIONS.corporate.read,
                  PERMISSIONS.catalog.read,
                  PERMISSIONS.catalog.corporateVisibility,
                ])}
              >
                <CatalogV2 />
              </ProtectedRoute>
            ),
          },
          {
            path: "catalog/drafts/:idDraft/edit",
            element: (
              <ProtectedRoute
                isAllowed={isInclude(
                  "corporate&&corporate.catalog&&learning_catalogue.learning_object.write",
                )}
              >
                <EditDraftV2 />
              </ProtectedRoute>
            ),
          },
          {
            path: "users-groups",
            element: (
              <ProtectedRoute
                isAllowed={
                  can(PERMISSIONS.corporate.read) &&
                  canAny([
                    PERMISSIONS.users.read,
                    PERMISSIONS.groups.static.read,
                    PERMISSIONS.groups.dynamic.read,
                  ])
                }
              >
                <UsersGroupsV2 />
              </ProtectedRoute>
            ),
          },
          {
            path: "users",
            element: <Navigate to="../users-groups" replace />,
          },
          {
            path: "users-groups/add",
            element: (
              <ProtectedRoute
                isAllowed={can([
                  PERMISSIONS.corporate.read,
                  PERMISSIONS.users.add,
                ])}
              >
                <AddUserV2 />
              </ProtectedRoute>
            ),
          },
          {
            path: "users-groups/:userId/edit",
            element: (
              <ProtectedRoute
                isAllowed={can([
                  PERMISSIONS.corporate.read,
                  PERMISSIONS.users.add,
                ])}
              >
                <EditUserV2 />
              </ProtectedRoute>
            ),
          },
          {
            path: "users-groups/groups/add",
            element: (
              <ProtectedRoute
                isAllowed={can([
                  PERMISSIONS.corporate.read,
                  PERMISSIONS.groups.static.write,
                ])}
              >
                <AddStaticGroupV2 />
              </ProtectedRoute>
            ),
          },
          {
            path: "users-groups/dynamic-groups/add",
            element: (
              <ProtectedRoute
                isAllowed={can([
                  PERMISSIONS.corporate.read,
                  PERMISSIONS.groups.dynamic.write,
                ])}
              >
                <AddDynamicGroupV2 />
              </ProtectedRoute>
            ),
          },
          {
            path: "users-groups/groups/:groupId/edit",
            element: (
              <ProtectedRoute
                isAllowed={can([
                  PERMISSIONS.corporate.read,
                  PERMISSIONS.groups.static.write,
                ])}
              >
                <EditStaticGroupV2 />
              </ProtectedRoute>
            ),
          },
          {
            path: "users-groups/dynamic-groups/:groupId/edit",
            element: (
              <ProtectedRoute
                isAllowed={can([
                  PERMISSIONS.corporate.read,
                  PERMISSIONS.groups.dynamic.write,
                ])}
              >
                <EditDynamicGroupV2 />
              </ProtectedRoute>
            ),
          },
          {
            path: "users-groups/groups/:groupId",
            element: (
              <ProtectedRoute
                isAllowed={can([
                  PERMISSIONS.corporate.read,
                  PERMISSIONS.groups.static.read,
                ])}
              >
                <StaticGroupDetailV2 />
              </ProtectedRoute>
            ),
          },
          {
            path: "users-groups/dynamic-groups/:groupId",
            element: (
              <ProtectedRoute
                isAllowed={can([
                  PERMISSIONS.corporate.read,
                  PERMISSIONS.groups.dynamic.read,
                ])}
              >
                <DynamicGroupDetailV2 />
              </ProtectedRoute>
            ),
          },
          {
            path: "users-groups/:userId",
            element: (
              <ProtectedRoute
                isAllowed={can([
                  PERMISSIONS.corporate.read,
                  PERMISSIONS.users.read,
                ])}
              >
                <UserDetailV2 />
              </ProtectedRoute>
            ),
          },
          {
            path: "catalog/activities/add",
            element: (
              <ProtectedRoute
                isAllowed={isInclude(
                  "corporate&&corporate.catalog&&learning_catalogue.learning_object.write",
                )}
              >
                <AddActivityV2 />
              </ProtectedRoute>
            ),
          },
          {
            path: "catalog/activities/:idLO/edit",
            element: (
              <ProtectedRoute
                isAllowed={
                  isInclude("corporate&&corporate.catalog") &&
                  canAny([
                    PERMISSIONS.catalog.write,
                    PERMISSIONS.catalog.learningObjectRead,
                  ])
                }
              >
                <EditActivityV2 />
              </ProtectedRoute>
            ),
          },
          {
            path: "catalog/activities/:idLO/duplicate",
            element: (
              <ProtectedRoute
                isAllowed={isInclude(
                  "corporate&&corporate.catalog&&learning_catalogue.learning_object.write",
                )}
              >
                <EditActivityV2 duplication />
              </ProtectedRoute>
            ),
          },
          {
            path: "catalog/activities/:idLO/editions",
            element: (
              // Il permesso editions.*.retrieve dipende dal tipo di aula ed è
              // verificato in EditionsListPage.
              <ProtectedRoute
                isAllowed={isInclude("corporate&&corporate.catalog")}
              >
                <EditionsListPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "catalog/activities/:idLO/editions/add",
            element: (
              // Il permesso editions.*.create dipende dal tipo (?kind=) ed è
              // verificato in AddEditionPage.
              <ProtectedRoute
                isAllowed={isInclude("corporate&&corporate.catalog")}
              >
                <AddEditionPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "catalog/activities/:idLO/editions/:editionId",
            element: (
              // Il permesso editions.*.retrieve dipende dal tipo (?kind=) ed è
              // verificato in EditionDetailPage.
              <ProtectedRoute
                isAllowed={isInclude("corporate&&corporate.catalog")}
              >
                <EditionDetailPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "catalog/activities/:idLO/editions/:editionId/edit",
            element: (
              // editions.*.retrieve (accesso) ed editions.*.update (modifica)
              // dipendono dal tipo (?kind=) e sono verificati in AddEditionPage.
              <ProtectedRoute
                isAllowed={isInclude("corporate&&corporate.catalog")}
              >
                <AddEditionPage />
              </ProtectedRoute>
            ),
          },
          {
            path: "catalog/courses/add",
            element: (
              <ProtectedRoute
                isAllowed={isInclude(
                  "corporate&&corporate.catalog&&learning_catalogue.learning_object.write",
                )}
              >
                <AddCourseV2 />
              </ProtectedRoute>
            ),
          },
          {
            path: "catalog/courses/:idCourse/edit",
            element: (
              <ProtectedRoute
                isAllowed={
                  isInclude("corporate&&corporate.catalog") &&
                  canAny([
                    PERMISSIONS.catalog.write,
                    PERMISSIONS.catalog.learningObjectRead,
                  ])
                }
              >
                <EditCourseV2 />
              </ProtectedRoute>
            ),
          },
          {
            path: "catalog/courses/:idCourse/duplicate",
            element: (
              <ProtectedRoute
                isAllowed={isInclude(
                  "corporate&&corporate.catalog&&learning_catalogue.learning_object.write",
                )}
              >
                <EditCourseV2 duplication />
              </ProtectedRoute>
            ),
          },
          {
            path: "catalog/paths/add",
            element: (
              <ProtectedRoute
                isAllowed={isInclude(
                  "corporate&&corporate.catalog&&learning_catalogue.learning_object.write",
                )}
              >
                <AddPathV2 />
              </ProtectedRoute>
            ),
          },
          {
            path: "catalog/paths/:idPath/edit",
            element: (
              <ProtectedRoute
                isAllowed={
                  isInclude("corporate&&corporate.catalog") &&
                  canAny([
                    PERMISSIONS.catalog.write,
                    PERMISSIONS.catalog.learningObjectRead,
                  ])
                }
              >
                <EditPathV2 />
              </ProtectedRoute>
            ),
          },
          {
            path: "catalog/paths/:idPath/duplicate",
            element: (
              <ProtectedRoute
                isAllowed={isInclude(
                  "corporate&&corporate.catalog&&learning_catalogue.learning_object.write",
                )}
              >
                <EditPathV2 duplication />
              </ProtectedRoute>
            ),
          },
          {
            path: "catalog/courses/:idCourse",
            element: (
              <ProtectedRoute
                isAllowed={can([
                  PERMISSIONS.corporate.read,
                  PERMISSIONS.catalog.read,
                  PERMISSIONS.catalog.corporateVisibility,
                ])}
              >
                <CatalogLODetail />
              </ProtectedRoute>
            ),
          },
          {
            path: "catalog/paths/:idPath",
            element: (
              <ProtectedRoute
                isAllowed={can([
                  PERMISSIONS.corporate.read,
                  PERMISSIONS.catalog.read,
                  PERMISSIONS.catalog.corporateVisibility,
                ])}
              >
                <CatalogLODetail />
              </ProtectedRoute>
            ),
          },
          {
            path: "catalog/activities/:idLO",
            element: (
              <ProtectedRoute
                isAllowed={can([
                  PERMISSIONS.corporate.read,
                  PERMISSIONS.catalog.read,
                  PERMISSIONS.catalog.corporateVisibility,
                ])}
              >
                <CatalogLODetail />
              </ProtectedRoute>
            ),
          },
          {
            path: "my-space",
            element: (
              <ProtectedRoute isAllowed={canSeeMySpace(user?.permissions)}>
                <MySpace />
              </ProtectedRoute>
            ),
          },
        ],
      },
    ],
  };
};

export default useV2Routes;
